/**
 * Formulario para puntear productos en pedidos/albaranes
 * Permite escanear códigos de barras y registrar unidades
 */
import React, { useRef, useMemo, useEffect } from "react";
import { BOTON_PRIMARIO_SX } from "../../../../components/ui/buttonStyles";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import NumbersIcon from "@mui/icons-material/Numbers";
import { debounce } from "@mui/material/utils";
import { FieldLabel } from "../ui";

const FormularioPuntear = ({ type, onSubmit, unitsRef, eanRef }) => {
    const localUnitsRef = useRef(null);
    const localEanRef = useRef(null);

    // Usar refs externas si se proporcionan, sino usar refs locales
    const units = unitsRef || localUnitsRef;
    const ean = eanRef || localEanRef;

    // Debounce memoizado (misma instancia entre renders): limpia los espacios
    // del código escaneado cuando termina la ráfaga de teclas del escáner
    const handleEanChange = useMemo(
        () => debounce((event) => {
            if (ean.current) {
                ean.current.value = event.target.value.replace(/\s/g, "");
            }
        }, 300),
        [ean]
    );

    // Cancelar el debounce pendiente al desmontar
    useEffect(() => () => handleEanChange.clear(), [handleEanChange]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (onSubmit) {
            onSubmit(e);
        }
    };

    return (
        <Card
            sx={{
                mt: 3,
                mb: 3,
                backgroundColor: "#fff",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2.5,
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.06)"
                    }}
                >
                    <QrCodeScannerIcon sx={{ fontSize: 20, color: "#cc071e" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#6c757d",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            mb: 0.25
                        }}
                    >
                        Formulario de preparación
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#212529",
                            lineHeight: 1.2
                        }}
                    >
                        Puntear {type}
                    </Typography>
                </Box>
            </Box>

            {/* Formulario */}
            <CardContent sx={{ p: 3 }}>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                    <Box>
                        <FieldLabel icon={NumbersIcon}>Unidades</FieldLabel>
                        <TextField
                            type="number"
                            defaultValue="1"
                            inputProps={{ min: 1 }}
                            inputRef={units}
                            fullWidth
                            variant="outlined"
                        />
                    </Box>

                    <Box>
                        <FieldLabel icon={QrCodeScannerIcon}>Escanear código de barras</FieldLabel>
                        <TextField
                            type="text"
                            placeholder="Código EAN..."
                            inputRef={ean}
                            onChange={handleEanChange}
                            fullWidth
                            variant="outlined"
                        />
                    </Box>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        startIcon={<AddCircleIcon />}
                        sx={BOTON_PRIMARIO_SX}
                    >
                        Añadir producto
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default FormularioPuntear;
